import { FC, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { ChalkPageShell } from "../../shared/component/ChalkPageShell/ChalkPageShell";
import { useChalkFlipFace } from "../../shared/component/ChalkBoardFlip/chalk-flip-context";
import { HomeBoardVideo } from "../HomePage/HomeBoardVideo";
import { Seo } from "../../shared/component/Seo/Seo";
import { cn } from "../../shared/lib/cn";
import { SITE_SHARE_IMAGE } from "../../shared/model/siteAssets";
import {
  fallbackTeamMembers,
  fetchSiteTeam,
  teamMemberIdFromHash,
  teamMemberName,
  teamPhotoPosition,
  teamPhotoSrc,
  teamPhotoZoom,
  type SiteTeamMember,
} from "../../shared/model/siteTeam";
import "./style.css";

function shuffleTeam(members: SiteTeamMember[]): SiteTeamMember[] {
  const next = [...members];
  for (let index = next.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    const current = next[index];
    next[index] = next[swapIndex];
    next[swapIndex] = current;
  }
  return next;
}

function applyTeamLineup(current: SiteTeamMember[], incoming: SiteTeamMember[]): SiteTeamMember[] {
  const incomingById = new Map(incoming.map((member) => [member.id, member]));
  const kept = current
    .map((member) => incomingById.get(member.id))
    .filter((member): member is SiteTeamMember => member !== undefined);
  const knownIds = new Set(kept.map((member) => member.id));
  const added = incoming.filter((member) => !knownIds.has(member.id));
  if (!kept.length) return shuffleTeam(incoming);
  return [...kept, ...added];
}

const AboutUs: FC = () => {
  const { pathname, hash } = useLocation();
  const requestedId = teamMemberIdFromHash(hash);
  const teamPath = decodeTeamPath(pathname);
  const onTeamPage = teamPath === "/команда" || teamPath === "/aboutus";
  const flip = useChalkFlipFace();
  const [items, setItems] = useState<SiteTeamMember[]>(() => shuffleTeam(fallbackTeamMembers));
  const [selectedIndex, setSelectedIndex] = useState(() =>
    indexForMember(items, teamMemberIdFromHash(window.location.hash)),
  );
  const [openingShuffled, setOpeningShuffled] = useState(false);
  const boardOpening = flip.active && flip.turning && !onTeamPage;

  if (boardOpening && !openingShuffled) {
    setOpeningShuffled(true);
    setItems((current) => shuffleTeam(current));
    setSelectedIndex(0);
  }
  if (!boardOpening && openingShuffled && !onTeamPage && !flip.turning) {
    setOpeningShuffled(false);
  }

  useEffect(() => {
    if (!requestedId) return;
    const index = items.findIndex((member) => member.id === requestedId);
    if (index >= 0) setSelectedIndex(index);
  }, [items, requestedId]);

  useEffect(() => {
    let cancelled = false;
    fetchSiteTeam().then((members) => {
      if (cancelled || !members?.length) return;
      setItems((current) => applyTeamLineup(current, members));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!onTeamPage) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (!event.key.startsWith("Arrow")) return;
      event.preventDefault();
      setSelectedIndex((current) => {
        if ((event.key === "ArrowUp" || event.key === "ArrowLeft") && current > 0) return current - 1;
        if ((event.key === "ArrowDown" || event.key === "ArrowRight") && current + 1 < items.length) return current + 1;
        return current;
      });
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [items.length, onTeamPage]);

  return (
    <ChalkPageShell mainClassName="chalk-page__main--team" showHomeBack showSectionNav={false}>
      <HomeBoardVideo />
      {onTeamPage && (
      <Seo
        title="Команда — Театр «Дофамин»"
        description="Актёры и команда театра «Дофамин»."
        canonicalPath="/команда"
        imageUrl={SITE_SHARE_IMAGE}
      />
      )}

      <h1 className="chalk-page__title">КОМАНДА</h1>
      <div className="chalk-page__rule" aria-hidden />

      {items[selectedIndex] && <TeamFocus member={items[selectedIndex]} />}

      {onTeamPage && !flip.turning && (
        <TeamSideIcons
          members={items}
          selectedIndex={selectedIndex}
          onSelect={setSelectedIndex}
        />
      )}
    </ChalkPageShell>
  );
};

function indexForMember(members: SiteTeamMember[], id: string) {
  if (!id) return 0;
  const index = members.findIndex((member) => member.id === id);
  return index >= 0 ? index : 0;
}

function decodeTeamPath(pathname: string) {
  try {
    return decodeURIComponent(pathname);
  } catch {
    return pathname;
  }
}

export const Component = AboutUs;

function TeamPhoto({
  className,
  src,
  alt,
  focus,
  zoom,
  fetchPriority,
}: {
  className: string;
  src: string;
  alt: string;
  focus: string;
  zoom: number;
  fetchPriority?: "high" | "auto";
}) {
  const imageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const image = imageRef.current;
    if (!image) return;
    image.style.setProperty("--photo-focus", focus);
    image.style.setProperty("--photo-zoom", String(zoom));
  }, [focus, zoom]);

  return (
    <img
      ref={imageRef}
      className={className}
      src={src}
      alt={alt}
      decoding="async"
      fetchPriority={fetchPriority}
    />
  );
}

function TeamTile({
  name,
  img,
  role,
  focus,
  zoom,
  active,
  onSelect,
}: {
  name: string;
  img: string;
  role: string;
  focus: string;
  zoom: number;
  active: boolean;
  onSelect: () => void;
}) {
  const title = name.trim();

  return (
    <button
      type="button"
      className={cn("chalk-team-tile", active && "chalk-team-tile--active")}
      role="listitem"
      aria-label={`${title}, ${role}`}
      aria-current={active ? "true" : undefined}
      onClick={onSelect}
    >
      <TeamPhoto className="chalk-team-tile__img" src={teamPhotoSrc(img)} alt="" focus={focus} zoom={zoom} />
    </button>
  );
}

const TEAM_PHOTO_BURN =
  "M0.018,0.012 C0.08,0.003,0.16,0.011,0.26,0.004 C0.38,0.001,0.46,0.013,0.58,0.005 C0.7,0,0.82,0.01,0.94,0.004 C0.97,0.003,0.992,0.008,0.988,0.016 C0.996,0.03,0.978,0.05,0.994,0.09 C1,0.16,0.972,0.22,0.992,0.3 C1,0.4,0.968,0.46,0.99,0.54 C1,0.64,0.974,0.7,0.994,0.78 C1,0.86,0.978,0.92,0.986,0.962 C0.978,0.986,0.94,0.996,0.86,0.988 C0.74,1,0.62,0.987,0.5,0.996 C0.36,1,0.24,0.988,0.14,0.996 C0.06,1,0.02,0.99,0.012,0.972 C0.004,0.94,0.02,0.88,0.006,0.8 C0,0.7,0.022,0.62,0.004,0.52 C0,0.42,0.024,0.34,0.006,0.24 C0,0.14,0.018,0.08,0.008,0.04 C0.006,0.022,0.01,0.016,0.018,0.012 Z";

function TeamFocus({ member }: { member: SiteTeamMember }) {
  const name = teamMemberName(member);
  const nameRef = useFitName(name);

  return (
    <div className="chalk-team-focus">
      <svg className="chalk-team-focus__burn" aria-hidden focusable="false">
        <defs>
          <clipPath id="chalk-team-photo-burn" clipPathUnits="objectBoundingBox">
            <path d={TEAM_PHOTO_BURN} />
          </clipPath>
        </defs>
      </svg>
      <div className="chalk-team-focus__photo">
        <TeamPhoto
          className="chalk-team-focus__img"
          src={teamPhotoSrc(member.img)}
          alt={name}
          focus={teamPhotoPosition(member)}
          zoom={teamPhotoZoom(member)}
          fetchPriority="high"
        />
      </div>
      <div className="chalk-team-focus__note">
        <p className="chalk-team-focus__kicker">{member.role}</p>
        <h2 className="chalk-team-focus__name" ref={nameRef}>
          <TeamName name={name} />
        </h2>
        <div className="chalk-page__rule" aria-hidden />
        <p className="chalk-team-focus__text">{member.note}</p>
      </div>
    </div>
  );
}

function useFitName(name: string) {
  const ref = useRef<HTMLHeadingElement>(null);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;

    const fit = () => fitNameToWidth(node);
    fit();
    const observer = new ResizeObserver(fit);
    const box = node.parentElement ?? node;
    observer.observe(box);
    return () => observer.disconnect();
  }, [name]);

  return ref;
}

function fitNameToWidth(name: HTMLElement) {
  const surname = name.querySelector(".chalk-team-focus__surname");
  const line = surname instanceof HTMLElement ? surname : name;
  name.style.fontSize = "";
  let size = Number.parseFloat(getComputedStyle(name).fontSize);
  const fits = () => line.scrollWidth <= name.clientWidth + 1;

  while (size > 14 && !fits()) {
    size -= 0.5;
    name.style.fontSize = `${size}px`;
  }
}

function TeamName({ name }: { name: string }) {
  const parts = name.trim().split(/\s+/);
  const surname = parts.length > 1 ? parts[parts.length - 1] : "";
  const given = parts.length > 1 ? parts.slice(0, -1).join(" ") : name;

  return (
    <>
      {given}
      {surname && (
        <>
          <br />
          <span className="chalk-team-focus__surname">{surname}</span>
        </>
      )}
    </>
  );
}

function TeamSideIcons({
  members,
  selectedIndex,
  onSelect,
}: {
  members: SiteTeamMember[];
  selectedIndex: number;
  onSelect: (index: number) => void;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const [horizontal, setHorizontal] = useState(
    () => window.matchMedia("(max-width: 899px)").matches,
  );
  const [canUp, setCanUp] = useState(false);
  const [canDown, setCanDown] = useState(false);

  const syncArrows = () => {
    const list = listRef.current;
    if (!list) return;
    const along = horizontal ? list.scrollLeft : list.scrollTop;
    const view = horizontal ? list.clientWidth : list.clientHeight;
    const full = horizontal ? list.scrollWidth : list.scrollHeight;
    setCanUp(along > 1);
    setCanDown(along + view < full - 1);
  };

  useEffect(() => {
    const query = window.matchMedia("(max-width: 899px)");
    const onChange = () => setHorizontal(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const list = listRef.current;
    const selected = list?.querySelector(".chalk-team-tile--active");
    if (selected instanceof HTMLElement) {
      selected.scrollIntoView({ block: "nearest", inline: "nearest" });
    }
    syncArrows();
    window.addEventListener("resize", syncArrows);
    return () => window.removeEventListener("resize", syncArrows);
  }, [selectedIndex, members.length, horizontal]);

  const scrollByTile = (direction: -1 | 1) => {
    const distance = direction * 50;
    if (horizontal) {
      listRef.current?.scrollBy({ left: distance, behavior: "smooth" });
      return;
    }
    listRef.current?.scrollBy({ top: distance, behavior: "smooth" });
  };

  return createPortal(
    <div className="chalk-team-sides">
      <div className="chalk-team-sides__col">
        {canUp && (
          <button
            type="button"
            className="chalk-team-sides__arrow"
            onClick={() => scrollByTile(-1)}
            aria-label={horizontal ? "Участники левее" : "Участники выше"}
          >
            {horizontal ? "←" : "↑"}
          </button>
        )}
        <div className="chalk-team-sides__list" role="list" ref={listRef} onScroll={syncArrows}>
          {members.map((member, index) => (
            <TeamTile
              key={member.id}
              name={teamMemberName(member)}
              img={member.img}
              role={member.role}
              focus={teamPhotoPosition(member)}
              zoom={teamPhotoZoom(member)}
              active={selectedIndex === index}
              onSelect={() => onSelect(index)}
            />
          ))}
        </div>
        {canDown && (
          <button
            type="button"
            className="chalk-team-sides__arrow"
            onClick={() => scrollByTile(1)}
            aria-label={horizontal ? "Участники правее" : "Участники ниже"}
          >
            {horizontal ? "→" : "↓"}
          </button>
        )}
      </div>
    </div>,
    document.body
  );
}
